import { Stack } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { DialogListener } from '@components/Dialog';
import { PatientPlanCard, makeEmptyPlan } from '@components/PatientPlans';
import type {
  PatientPlanValue,
  PatientPlanValues,
} from '@components/PatientPlans';
import RoundedButton from '@components/RoundedButton';

import type { OrgFeatureModel } from 'generated/types';

const messages = defineMessages({
  title: {
    id: 'orgSettings.licensePlan.editPlan',
    defaultMessage: 'Edit patient plan',
  },
  done: {
    id: 'GLOBAL.done',
    defaultMessage: 'Done',
  },
});

interface CaptureCloseProps {
  fn: () => void;
  into: React.MutableRefObject<() => void>;
}

/**
 * `DialogListener` hands `handleClose` to its children alone, but Done and the
 * card's Remove sit in `actions`/`content`. Parking it in a ref from an effect
 * is what lets them close the dialog they are inside.
 */
function CaptureClose({ fn, into }: CaptureCloseProps) {
  React.useEffect(() => {
    into.current = fn;
  });
  return null;
}

interface PlanDraftContentProps {
  /** Position of the plan being edited within the draft */
  index: number;
  eligibleFeatures: OrgFeatureModel[];
  onRemove: () => void;
  disabled?: boolean;
}

/**
 * The card, bound to the dialog's draft rather than to the page's form. The
 * draft mirrors the page's shape, so the badge number and the "Copy from…"
 * list read exactly as they would on the page.
 *
 * `index` always addresses a plan here: the draft and the index are set
 * together on open, and Remove writes to the page's form rather than to the
 * draft, so the card cannot delete the row out from under itself.
 */
function PlanDraftContent({
  index,
  eligibleFeatures,
  onRemove,
  disabled,
}: PlanDraftContentProps) {
  const { setFieldValue, values } = useFormikContext<PatientPlanValues>();

  const plan = values.plans[index];
  const namePrefix = `plans[${index}]`;

  return (
    <Stack data-testid="plan-edit-dialog">
      <PatientPlanCard
        disabled={disabled}
        eligibleFeatures={eligibleFeatures}
        expanded
        index={index}
        namePrefix={namePrefix}
        onCopyFrom={(source) =>
          setFieldValue(`${namePrefix}.feature_ids`, [...source.feature_ids])
        }
        onRemove={onRemove}
        onSetFeatureIds={(featureIds) =>
          setFieldValue(`${namePrefix}.feature_ids`, featureIds)
        }
        onToggleExpanded={() => undefined}
        plan={plan}
        plans={values.plans}
      />
    </Stack>
  );
}

/** How the enclosing view opens the editor. */
type PlanEditTriggers = {
  /** Open the editor on an existing plan */
  editPlan: (index: number) => void;
  /** Open the editor on a new plan, uncommitted until Done */
  addPlan: () => void;
};

interface PlanEditDialogProps {
  eligibleFeatures: OrgFeatureModel[];
  children: (triggers: PlanEditTriggers) => React.ReactNode;
  disabled?: boolean;
}

/**
 * Wraps one `PatientPlanCard` in a dialog so the counts table stays the primary
 * view, and owns the table that opens it — `DialogListener` keeps `open` to
 * itself and only reaches its children.
 *
 * The card edits a draft copy of the plans rather than the page's form, so
 * closing the dialog discards: nothing is written until Done. That is also why
 * "Add patient plan type" leaves no half-filled row behind when it is
 * abandoned.
 */
export default function PlanEditDialog({
  eligibleFeatures,
  children,
  disabled,
}: PlanEditDialogProps) {
  const { formatMessage } = useIntl();
  const { setFieldValue, values } = useFormikContext<PatientPlanValues>();

  const [editIndex, setEditIndex] = React.useState(0);
  // Captured once per open so the reference holds steady while the dialog is
  // up. Deriving it each render would give `DraftForm` a new identity, and
  // remount the draft, on every keystroke.
  const [draftPlans, setDraftPlans] = React.useState<PatientPlanValue[]>([]);
  const closeRef = React.useRef<() => void>(() => undefined);

  const handleCommit = React.useCallback(
    (draft: PatientPlanValues) => {
      // The whole array, not just the edited plan: the dialog is modal, so no
      // other plan can have moved while it was open.
      setFieldValue('plans', draft.plans);
      closeRef.current();
    },
    [setFieldValue]
  );

  /** Formik scope for the draft, wrapping the title, content and actions. */
  const DraftForm = React.useMemo(
    () =>
      function DraftForm({ children: scoped }: React.PropsWithChildren) {
        return (
          <Formik initialValues={{ plans: draftPlans }} onSubmit={handleCommit}>
            <Form>{scoped}</Form>
          </Formik>
        );
      },
    [draftPlans, handleCommit]
  );

  function handleRemove() {
    // A plan added this session exists only in the draft, so filtering it out
    // leaves the page's list as it was — one branch covers both cases.
    setFieldValue(
      'plans',
      draftPlans.filter((_, i) => i !== editIndex)
    );
    closeRef.current();
  }

  function openEditor(
    index: number,
    plans: PatientPlanValue[],
    handleOpen: () => void
  ) {
    setDraftPlans(plans);
    setEditIndex(index);
    handleOpen();
  }

  return (
    <DialogListener
      actions={
        <RoundedButton autoSize margin={0} type="submit">
          {formatMessage(messages.done)}
        </RoundedButton>
      }
      content={
        <PlanDraftContent
          disabled={disabled}
          eligibleFeatures={eligibleFeatures}
          index={editIndex}
          onRemove={handleRemove}
        />
      }
      // The card's feature grid caps at three columns and only reaches them
      // past ~700px, so the default `sm` paper wraps them. `md` is where the
      // grid stops wrapping; wider only adds margin (VEG-828).
      dialogProps={{ fullWidth: true, maxWidth: 'md' }}
      persistent
      title={formatMessage(messages.title)}
      Wrapper={DraftForm}
    >
      {({ handleOpen, handleClose }) => (
        <>
          <CaptureClose fn={handleClose} into={closeRef} />
          {children({
            addPlan: () => {
              const next = [...values.plans, makeEmptyPlan()];
              openEditor(next.length - 1, next, handleOpen);
            },
            editPlan: (index) => openEditor(index, values.plans, handleOpen),
          })}
        </>
      )}
    </DialogListener>
  );
}

export type { PlanEditTriggers };
